import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Avatar,
  Stack,
  useTheme,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Work,
  People,
  TrendingUp,
  Schedule,
  ArrowUpward,
  Refresh,
  CheckCircle,
  Cancel,
  HourglassEmpty,
  Phone,
  Email
} from '@mui/icons-material';
import { supabase } from '../../lib/supabaseClient';
import { getAuthUser } from '../../services/authService';
import { useEntity } from '../../context/EntityContext';
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

interface DashboardStats {
  totalJobs: number;
  activeJobs: number;
  totalApplications: number;
  newApplications: number;
  conversionRate: number;
  avgTimeToHire: number;
  pendingApplications: number;
  reviewingApplications: number;
  shortlistedApplications: number;
  rejectedApplications: number;
  hiredApplications: number;
}

interface JobPerformance {
  jobTitle: string;
  applicationsCount: number;
  hiredCount: number;
  conversionRate: number;
}

interface ApplicationTrend {
  date: string;
  applications: number;
  hired: number;
}

export const AnalyticsPage: React.FC = () => {
  const theme = useTheme();
  const { entity } = useEntity();
  const [stats, setStats] = useState<DashboardStats>({
    totalJobs: 0,
    activeJobs: 0,
    totalApplications: 0,
    newApplications: 0,
    conversionRate: 0,
    avgTimeToHire: 0,
    pendingApplications: 0,
    reviewingApplications: 0,
    shortlistedApplications: 0,
    rejectedApplications: 0,
    hiredApplications: 0
  });
  const [jobPerformance, setJobPerformance] = useState<JobPerformance[]>([]);
  const [applicationTrends, setApplicationTrends] = useState<ApplicationTrend[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<'7days' | '30days' | '90days' | 'all'>('30days');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const user = await getAuthUser();
      if (!user) {
        setError('User not authenticated');
        return;
      }

      // Check if user is HR or Admin
      const { data: userData } = await supabase
        .from('users')
        .select('role')
        .eq('id', user.id)
        .single();

      if (!userData || (userData.role !== 'hr' && userData.role !== 'admin')) {
        setError('Access denied. This page is only accessible to HR and Admin users.');
        setLoading(false);
        return;
      }

      const entityId = user.entityId;

      // Calculate date range
      let dateFilter: Date | null = null;
      if (timeRange !== 'all') {
        dateFilter = new Date();
        const days = timeRange === '7days' ? 7 : timeRange === '30days' ? 30 : 90;
        dateFilter.setDate(dateFilter.getDate() - days);
      }

      // Fetch total jobs
      let jobsQuery = supabase
        .from('jobs')
        .select('*', { count: 'exact', head: true })
        .eq('entity_id', entityId)
        .is('deleted_at', null);

      if (dateFilter) {
        jobsQuery = jobsQuery.gte('created_at', dateFilter.toISOString());
      }

      const { count: totalJobsCount } = await jobsQuery;

      // Fetch active jobs
      const { count: activeJobsCount } = await supabase
        .from('jobs')
        .select('*', { count: 'exact', head: true })
        .eq('entity_id', entityId)
        .eq('status', 'active')
        .is('deleted_at', null);

      // Fetch total applications with status filter
      let applicationsQuery = supabase
        .from('applications')
        .select('*', { count: 'exact', head: true })
        .eq('entity_id', entityId)
        .is('deleted_at', null);

      if (dateFilter) {
        applicationsQuery = applicationsQuery.gte('submitted_at', dateFilter.toISOString());
      }

      if (statusFilter !== 'all') {
        applicationsQuery = applicationsQuery.eq('status', statusFilter);
      }

      const { count: totalApplicationsCount } = await applicationsQuery;

      // Fetch new applications (last 7 days)
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      const { count: newApplicationsCount } = await supabase
        .from('applications')
        .select('*', { count: 'exact', head: true })
        .eq('entity_id', entityId)
        .gte('submitted_at', oneWeekAgo.toISOString())
        .is('deleted_at', null);

      // Fetch applications by status
      const { count: pendingCount } = await supabase
        .from('applications')
        .select('*', { count: 'exact', head: true })
        .eq('entity_id', entityId)
        .eq('status', 'pending')
        .is('deleted_at', null);

      const { count: reviewingCount } = await supabase
        .from('applications')
        .select('*', { count: 'exact', head: true })
        .eq('entity_id', entityId)
        .eq('status', 'reviewing')
        .is('deleted_at', null);

      const { count: shortlistedCount } = await supabase
        .from('applications')
        .select('*', { count: 'exact', head: true })
        .eq('entity_id', entityId)
        .eq('status', 'shortlisted')
        .is('deleted_at', null);

      const { count: rejectedCount } = await supabase
        .from('applications')
        .select('*', { count: 'exact', head: true })
        .eq('entity_id', entityId)
        .eq('status', 'rejected')
        .is('deleted_at', null);

      const { count: hiredCount } = await supabase
        .from('applications')
        .select('*', { count: 'exact', head: true })
        .eq('entity_id', entityId)
        .eq('status', 'hired')
        .is('deleted_at', null);

      // Calculate conversion rate
      const conversionRate = totalApplicationsCount && totalApplicationsCount > 0
        ? Math.round((hiredCount || 0) / totalApplicationsCount * 100 * 10) / 10
        : 0;

      // Fetch applications with hire dates to calculate avg time to hire
      const { data: hiredApplications } = await supabase
        .from('applications')
        .select('submitted_at, reviewed_at')
        .eq('entity_id', entityId)
        .eq('status', 'hired')
        .not('reviewed_at', 'is', null)
        .is('deleted_at', null)
        .limit(50);

      let avgTimeToHire = 0;
      if (hiredApplications && hiredApplications.length > 0) {
        const totalDays = hiredApplications.reduce((sum, app) => {
          const submitted = new Date(app.submitted_at);
          const reviewed = new Date(app.reviewed_at);
          const days = Math.ceil((reviewed.getTime() - submitted.getTime()) / (1000 * 60 * 60 * 24));
          return sum + days;
        }, 0);
        avgTimeToHire = Math.round(totalDays / hiredApplications.length);
      }

      // Fetch job performance data
      const { data: jobsData } = await supabase
        .from('jobs')
        .select(`
          id,
          title,
          applications:applications(count)
        `)
        .eq('entity_id', entityId)
        .is('deleted_at', null)
        .limit(10);

      const jobPerformanceData: JobPerformance[] = [];
      if (jobsData) {
        for (const job of jobsData) {
          const { count: hiredForJob } = await supabase
            .from('applications')
            .select('*', { count: 'exact', head: true })
            .eq('job_id', job.id)
            .eq('status', 'hired')
            .is('deleted_at', null);

          const applicationsCount = job.applications?.[0]?.count || 0;
          const jobHiredCount = hiredForJob || 0;
          const jobConversionRate = applicationsCount > 0
            ? Math.round((jobHiredCount / applicationsCount) * 100 * 10) / 10
            : 0;

          jobPerformanceData.push({
            jobTitle: job.title,
            applicationsCount,
            hiredCount: jobHiredCount,
            conversionRate: jobConversionRate
          });
        }
      }

      // Fetch application trends (last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data: trendsData } = await supabase
        .from('applications')
        .select('submitted_at, status')
        .eq('entity_id', entityId)
        .gte('submitted_at', thirtyDaysAgo.toISOString())
        .is('deleted_at', null)
        .order('submitted_at', { ascending: true });

      const trendsByDate: { [key: string]: { applications: number; hired: number } } = {};
      if (trendsData) {
        trendsData.forEach(app => {
          const date = new Date(app.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          if (!trendsByDate[date]) {
            trendsByDate[date] = { applications: 0, hired: 0 };
          }
          trendsByDate[date].applications++;
          if (app.status === 'hired') {
            trendsByDate[date].hired++;
          }
        });
      }

      const applicationTrendsData: ApplicationTrend[] = Object.entries(trendsByDate).map(([date, data]) => ({
        date,
        applications: data.applications,
        hired: data.hired
      }));

      setStats({
        totalJobs: totalJobsCount || 0,
        activeJobs: activeJobsCount || 0,
        totalApplications: totalApplicationsCount || 0,
        newApplications: newApplicationsCount || 0,
        conversionRate,
        avgTimeToHire,
        pendingApplications: pendingCount || 0,
        reviewingApplications: reviewingCount || 0,
        shortlistedApplications: shortlistedCount || 0,
        rejectedApplications: rejectedCount || 0,
        hiredApplications: hiredCount || 0
      });

      setJobPerformance(jobPerformanceData);
      setApplicationTrends(applicationTrendsData);

    } catch (err: any) {
      console.error('Error fetching analytics:', err);
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [timeRange, statusFilter]);

  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchAnalytics, entity?.id]);

  const statusDistributionData = [
    { name: 'Pending', value: stats.pendingApplications, color: '#FFA726' },
    { name: 'Reviewing', value: stats.reviewingApplications, color: '#42A5F5' },
    { name: 'Shortlisted', value: stats.shortlistedApplications, color: '#66BB6A' },
    { name: 'Rejected', value: stats.rejectedApplications, color: '#EF5350' },
    { name: 'Hired', value: stats.hiredApplications, color: '#26A69A' }
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={50} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Paper sx={{ p: 4, borderRadius: 3, border: `2px solid ${theme.palette.error.main}` }}>
          <Typography variant="h6" color="error" gutterBottom>
            {error.includes('Access denied') ? 'Access Denied' : 'Error Loading Analytics'}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {error}
          </Typography>
          {!error.includes('Access denied') && (
            <Button variant="contained" onClick={fetchAnalytics}>
              Retry
            </Button>
          )}
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with Filters */}
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Analytics Dashboard
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Comprehensive insights and performance metrics
            </Typography>
          </Box>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Time Range</InputLabel>
              <Select
                value={timeRange}
                label="Time Range"
                onChange={(e) => setTimeRange(e.target.value as any)}
              >
                <MenuItem value="7days">Last 7 Days</MenuItem>
                <MenuItem value="30days">Last 30 Days</MenuItem>
                <MenuItem value="90days">Last 90 Days</MenuItem>
                <MenuItem value="all">All Time</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={statusFilter}
                label="Status Filter"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="reviewing">Reviewing</MenuItem>
                <MenuItem value="shortlisted">Shortlisted</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="hired">Hired</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchAnalytics}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* Key Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{
            background: '#ffffff',
            border: `1px solid #e0e0e0`,
            borderRadius: 2,
            transition: 'all 0.3s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="flex-start" spacing={2}>
                <Avatar sx={{
                  bgcolor: theme.palette.primary.main,
                  width: 56,
                  height: 56
                }}>
                  <Work sx={{ fontSize: 28 }} />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                    Total Jobs
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 800, mb: 1, color: theme.palette.primary.main }}>
                    {stats.totalJobs}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <ArrowUpward sx={{ fontSize: 16, color: 'success.main' }} />
                    <Typography variant="caption" color="success.main" sx={{ fontWeight: 700 }}>
                      {stats.activeJobs} active
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{
            background: '#ffffff',
            border: `1px solid #e0e0e0`,
            borderRadius: 2,
            transition: 'all 0.3s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="flex-start" spacing={2}>
                <Avatar sx={{
                  bgcolor: '#10b981',
                  width: 56,
                  height: 56
                }}>
                  <People sx={{ fontSize: 28 }} />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                    Applications
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 800, mb: 1, color: '#10b981' }}>
                    {stats.totalApplications}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <ArrowUpward sx={{ fontSize: 16, color: '#10b981' }} />
                    <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 700 }}>
                      +{stats.newApplications} this week
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{
            background: '#ffffff',
            border: `1px solid #e0e0e0`,
            borderRadius: 2,
            transition: 'all 0.3s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="flex-start" spacing={2}>
                <Avatar sx={{
                  bgcolor: theme.palette.secondary.main,
                  width: 56,
                  height: 56
                }}>
                  <TrendingUp sx={{ fontSize: 28 }} />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                    Conversion Rate
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 800, mb: 1, color: theme.palette.secondary.main }}>
                    {stats.conversionRate}%
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      {stats.hiredApplications} hired
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{
            background: '#ffffff',
            border: `1px solid #e0e0e0`,
            borderRadius: 2,
            transition: 'all 0.3s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="flex-start" spacing={2}>
                <Avatar sx={{
                  bgcolor: '#8B6BB1',
                  width: 56,
                  height: 56
                }}>
                  <Schedule sx={{ fontSize: 28 }} />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                    Avg. Days to Hire
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 800, mb: 1, color: '#8B6BB1' }}>
                    {stats.avgTimeToHire}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      Industry avg: 24
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Application Status Distribution & Trends */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Status Distribution Pie Chart */}
        <Grid item xs={12} md={6}>
          <Card sx={{ background: '#ffffff', border: `1px solid #e0e0e0`, borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Application Status Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={statusDistributionData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => `${entry.name}: ${entry.value}`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {statusDistributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Application Trends Line Chart */}
        <Grid item xs={12} md={6}>
          <Card sx={{ background: '#ffffff', border: `1px solid #e0e0e0`, borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Application Trends (Last 30 Days)
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={applicationTrends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="applications" stroke="#8884d8" strokeWidth={2} name="Applications" />
                  <Line type="monotone" dataKey="hired" stroke="#82ca9d" strokeWidth={2} name="Hired" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Job Performance Table */}
      <Card sx={{ background: '#ffffff', border: `1px solid #e0e0e0`, borderRadius: 2, mb: 4 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Job Performance Analysis
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Job Title</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Applications</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Hired</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Conversion Rate</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {jobPerformance.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No job performance data available</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  jobPerformance.map((job, index) => (
                    <TableRow key={index} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {job.jobTitle}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Chip label={job.applicationsCount} size="small" color="primary" variant="outlined" />
                      </TableCell>
                      <TableCell align="right">
                        <Chip label={job.hiredCount} size="small" color="success" variant="outlined" />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600} color={job.conversionRate >= 10 ? 'success.main' : 'text.primary'}>
                          {job.conversionRate}%
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Detailed Status Breakdown */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper sx={{ p: 2, textAlign: 'center', background: '#ffffff', border: `1px solid #e0e0e0` }}>
            <HourglassEmpty sx={{ fontSize: 40, color: '#FFA726', mb: 1 }} />
            <Typography variant="h4" fontWeight={700}>{stats.pendingApplications}</Typography>
            <Typography variant="caption" color="text.secondary">Pending</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper sx={{ p: 2, textAlign: 'center', background: '#ffffff', border: `1px solid #e0e0e0` }}>
            <Email sx={{ fontSize: 40, color: '#42A5F5', mb: 1 }} />
            <Typography variant="h4" fontWeight={700}>{stats.reviewingApplications}</Typography>
            <Typography variant="caption" color="text.secondary">Reviewing</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper sx={{ p: 2, textAlign: 'center', background: '#ffffff', border: `1px solid #e0e0e0` }}>
            <CheckCircle sx={{ fontSize: 40, color: '#66BB6A', mb: 1 }} />
            <Typography variant="h4" fontWeight={700}>{stats.shortlistedApplications}</Typography>
            <Typography variant="caption" color="text.secondary">Shortlisted</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper sx={{ p: 2, textAlign: 'center', background: '#ffffff', border: `1px solid #e0e0e0` }}>
            <Cancel sx={{ fontSize: 40, color: '#EF5350', mb: 1 }} />
            <Typography variant="h4" fontWeight={700}>{stats.rejectedApplications}</Typography>
            <Typography variant="caption" color="text.secondary">Rejected</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper sx={{ p: 2, textAlign: 'center', background: '#ffffff', border: `1px solid #e0e0e0` }}>
            <Phone sx={{ fontSize: 40, color: '#26A69A', mb: 1 }} />
            <Typography variant="h4" fontWeight={700}>{stats.hiredApplications}</Typography>
            <Typography variant="caption" color="text.secondary">Hired</Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
